import { useEffect, useRef, useState } from "react"
import { Check } from "lucide-react"

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { BarButton, CountBadge } from "./FilterBarButton"

// Picks made in quick succession are sent as one search once the customer
// pauses, so ticking three topics costs one server round trip, not three.
export const TOPIC_COMMIT_MS = 250

const GROUP =
  "p-0 [&_[cmdk-group-heading]]:px-[11px] [&_[cmdk-group-heading]]:pt-[7px] [&_[cmdk-group-heading]]:pb-[6px] [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:tracking-[0.09em] [&_[cmdk-group-heading]]:text-ink-3 [&_[cmdk-group-heading]]:uppercase"

// Substring, not cmdk's fuzzy score: "rom" should find Romance, not every
// label that happens to contain an r, an o and an m in order.
const containsFilter = (value, search) => (value.toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0)

const sameList = (a, b) => a.length === b.length && a.every((k) => b.includes(k))

// The Topic dropdown: one searchable multi-select over every niche. The common
// topics lead; a topic picked from the long tail floats into "Selected" so an
// arrival filter (an order's invite link pre-applies Plagiarism Pro) is on
// screen and ticked, not buried in the A–Z list.
export default function TopicFilter({ niches, applied, onChange }) {
  const all = [...niches.quick, ...niches.more]
  const labelOf = (key) => all.find((n) => n.key === key)?.label || key
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [picked, setPicked] = useState(applied)
  const timer = useRef(null)
  const latest = useRef(applied)
  latest.current = applied

  // Follow the server's applied set (a chip removed, Clear all) unless a pick
  // of our own is still waiting to be sent.
  const appliedKey = applied.join(",")
  useEffect(() => {
    if (!timer.current) setPicked(latest.current)
  }, [appliedKey])

  useEffect(() => () => clearTimeout(timer.current), [])

  const commit = (list) => {
    clearTimeout(timer.current)
    timer.current = null
    if (!sameList(list, latest.current)) onChange({ niches: list })
  }

  const toggle = (key) => {
    const next = picked.includes(key) ? picked.filter((k) => k !== key) : [...picked, key]
    setPicked(next)
    if (query) setQuery("")
    clearTimeout(timer.current)
    timer.current = setTimeout(() => commit(next), TOPIC_COMMIT_MS)
  }

  const onOpenChange = (next) => {
    setOpen(next)
    if (!next) {
      setQuery("")
      if (timer.current) commit(picked)
    }
  }

  const clear = () => {
    setPicked([])
    commit([])
  }

  const quickKeys = niches.quick.map((n) => n.key)
  const selectedTail = picked.filter((k) => !quickKeys.includes(k))
  const rest = niches.more.filter((n) => !picked.includes(n.key))

  const item = (key, label) => {
    const on = picked.includes(key)
    return (
      <CommandItem
        key={key}
        value={label}
        onSelect={() => toggle(key)}
        className={cn("gap-[11px] rounded-[9px] px-[11px] py-[10px] text-[13.5px]", on ? "font-semibold" : "font-medium")}
      >
        <span
          className={cn(
            "flex size-[16px] shrink-0 items-center justify-center rounded-[4px] border-[1.5px]",
            on ? "border-foreground bg-foreground text-background" : "border-line-2 text-transparent"
          )}
        >
          <Check className="size-[11px] text-current" strokeWidth={3} />
        </span>
        {label}
      </CommandItem>
    )
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <BarButton has={picked.length > 0}>
          <span className="max-w-[170px] truncate max-[561px]:max-w-full">
            {picked.length === 1 ? labelOf(picked[0]) : "Topic"}
          </span>
          {picked.length > 1 && <CountBadge n={picked.length} />}
        </BarButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={7}
        collisionPadding={14}
        className="flex w-[300px] flex-col rounded-[13px] border-border bg-card p-0 shadow-[0_20px_46px_-16px_rgba(0,0,0,0.34)] max-[561px]:w-[calc(100vw-28px)]"
      >
        <Command filter={containsFilter}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={`Search ${all.length} topics…`}
            className="text-[13.5px]"
          />
          <CommandList className="max-h-[320px] px-[7px] py-[6px]">
            <CommandEmpty className="px-[11px] py-[18px] text-center text-[13px] text-ink-3">
              No topics match “{query}”
            </CommandEmpty>
            {selectedTail.length > 0 && (
              <CommandGroup heading="Selected" className={GROUP}>
                {selectedTail.map((key) => item(key, labelOf(key)))}
              </CommandGroup>
            )}
            <CommandGroup heading="Common" className={GROUP}>
              {niches.quick.map((n) => item(n.key, n.label))}
            </CommandGroup>
            <CommandGroup heading="All topics" className={GROUP}>
              {rest.map((n) => item(n.key, n.label))}
            </CommandGroup>
          </CommandList>
        </Command>
        {picked.length > 0 && (
          <div className="flex items-center justify-between border-t border-border px-[14px] py-[10px] text-[12px] font-semibold text-ink-3">
            <span>{picked.length} selected</span>
            <button type="button" onClick={clear} className="text-muted-foreground hover:text-foreground">
              Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

